declare var $: any;
//system
import { Component, ViewChild, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { NgbModal, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Location } from '@angular/common';
import { Router } from '@angular/router';

//third-party
import { createEvent, DateArray, EventAttributes } from 'ics';
import { v4 as uuidv4 } from 'uuid';

//services
import { LogService } from 'src/app/services/common/log.service';
import { StudioService } from 'src/app/services/studio.service';
import { UserService } from 'src/app/services/user.service';
import { AuthService } from 'src/app/services/auth.service';
import { InviteCodeService } from 'src/app/services/invite-code.service';

//components
import { LoginComponent } from 'src/app/components/df-login/df-login.component';
import { InviteComponent } from 'src/app/components/invite/invite.component';

//interfaces
import { MemberModel } from 'src/app/models/member';
import { SessionDetails } from 'src/app/models/studio/session-details';
import { ClassMemberModel, ClassBookingRequest } from 'src/app/models/studio/class-booking';
import { OpenGraphMetadata, InviteCode, InviteCodeGenerateRequest, InviteDialogModel, InviteCodeRedeemRequest } from 'src/app/models/invite-code';

//utils
import { Utils } from 'src/app/services/common/utils';
import { UserType } from 'src/app/models/enums';
import { InviteCodeIntent } from 'src/app/models/enums';
import { AlertService } from 'src/app/services/alert.service';
import { EnvironmentService } from 'src/app/services/environment.service';
import { FacilityService } from 'src/app/services/facility.service';

declare const google: any;

@Component({
  selector: 'app-confirm-reservation',
  templateUrl: './confirm-reservation.component.html',
  styleUrls: ['./confirm-reservation.component.scss']
})
export class ConfirmReservationComponent implements OnInit {
  @ViewChild('login') loginPopup!: LoginComponent;
  address         : string = '';
  directionUrl    : string = '';
  sessionTiming   : string = '';
  classMember!    : ClassMemberModel;
  person          : any;
  submitted       : boolean  = false;
  inviteSubmitted : boolean  = false;
  inviteCode!     : InviteCode;
  currentMember!  : MemberModel;
  inviteDialogModel!: InviteDialogModel;
  openGraphMetadata!: OpenGraphMetadata;
  showSpots: boolean = true;
  allowInviteFriends: boolean = false;

  @Input() public sessionDetails! : SessionDetails;
  @Input() public selectedSpots! : number[];
  @Input() public membershipPaused: boolean = false;
  @Output() passEntry: EventEmitter<ClassMemberModel> = new EventEmitter();


  constructor(
    public activeModal: NgbActiveModal
    , private router: Router
    , private location: Location
    , private modalService: NgbModal
    , private logService: LogService
    , private studioService: StudioService
    , private userService: UserService
    , private authService: AuthService
    , private inviteCodeService: InviteCodeService
    , private alertService: AlertService
    , private _environmentService: EnvironmentService
    , private facilityService: FacilityService
  ) 
  {
    
  }

  private async getSessionTimeZone(): Promise<string> {
    let locations: { Id: number; TimeZone?: string }[] = [];
    try {
      locations = await this.facilityService.getLocations();
    } catch {
      locations = [];
    }
    return Utils.resolveSessionTimeZoneFromLocations(
      this.sessionDetails?.Studio?.LocationId,
      locations,
      this._environmentService.getFacilityConfig()?.timezone
    );
  }

  async ngOnInit() {
    this.address = await this.getAddress();
    this.directionUrl = this.getDirectionUrl();
    this.currentMember = this.authService.getLoggedInMember();
    this.person = this.userService.getUser();
    const sessionTimeZone = await this.getSessionTimeZone();
    const display = Utils.formatSessionDisplayParts(
      this.sessionDetails.StartDate,
      this.sessionDetails.StartTime,
      this.sessionDetails.EndTime,
      sessionTimeZone
    );
    this.sessionTiming = display.timeZoneAbbr
      ? `${display.dayOfWeek}, ${display.month} ${display.dayOfMonth} at ${display.startTimeFull} ${display.timeZoneAbbr}`
      : `${display.dayOfWeek}, ${display.month} ${display.dayOfMonth} at ${display.startTimeFull}`;
    if(!this.sessionDetails.Class.ShowSpotSelection)
    {
      this.showSpots = false;
    }
    this.allowInviteFriends = this._environmentService.getFacilityConfig().allowInviteFriends;
  }

  goBack() {
    this.activeModal.close(null);
  }

  closeModal() {
    this.passEntry.emit(this.classMember);
    this.activeModal.close(this.classMember);
  }

  reserveSpot() {
    this.logService.debug("reserve spot");
    if (this.membershipPaused) {
      this.alertService.error(
        'Your membership is paused. Resume your subscription to book classes.',
        true,
        true
      );
      return;
    }

    this.submitted = true;
    var isLoggedIn : boolean = this.authService.isLoggedIn();
    if(!isLoggedIn)
    {
      this.loginPopup.open(this.sessionDetails.Id);
      return;
    }

    var member: any = this.userService.getMember();
      const bookingRequest: ClassBookingRequest = {
        SessionId: this.sessionDetails.Id,
        MemberId: member.Id,
        Spot: this.selectedSpots[0]
      };

      this.studioService.bookSpot(bookingRequest).subscribe((data: ClassMemberModel | string | any) => {
        if(typeof data === 'string') {
          this.logService.debug("data", data);
          if(data == "NOT_ENOUGH_CREDITS")
          {
            this.alertService.error("You do not have enough credits to book a class.", true, true);
            this.submitted = false;
            
            // Redirect to purchase page with redirectTo query parameter
            this.closeModal();
            const redirectTo = `/studio/class-details/${this.sessionDetails.Id}`;
            const currencyId = this.sessionDetails.Class.CurrencyId;
            this.router.navigate(['/purchase'], { queryParams: { redirectTo, currencyId } });
            return;
          }

          if(data == "Spot is already taken.")
          {
            this.alertService.error("Spot is already taken.");
            this.submitted = false;
            this.closeModal();
            return;
          }

          if(data == "Class is cancelled.")
          {
            this.alertService.error("Class is cancelled.");
            this.submitted = false;
            this.closeModal();
            return;
          }

          if(data.startsWith("Schedule conflict detected."))
          {
            // window.alert(data);
            console.error(data);
            this.alertService.error('Schedule conflict.');
            this.submitted = false;
            this.closeModal();
            return;
          }

          // Handle other string error messages (e.g., "Class booking for free trial member is not allowed after free trial days.")
          this.alertService.error(data);
          this.submitted = false;
          this.closeModal();
          return;
        } else if (data && typeof data === 'object' && 'Id' in data && data.Id) {
          // Success response - ClassMemberModel (has Id property)
          this.logService.debug("typeof data", typeof data);
          this.classMember = data as ClassMemberModel;
          this.logService.debug("bookSpot response", this.classMember);
          this.submitted = false;
          this.closeModal();
        } else {
          // Handle error response object (when wantResponseAnyHow returns error as object)
          const errorMessage = this.extractErrorMessage(data);
          this.alertService.error(errorMessage);
          this.submitted = false;
          this.closeModal();
        }
      });
  }

  async inviteFriends() {
    this.inviteSubmitted = true;

    this.inviteCodeService.generateInviteCode(new InviteCodeGenerateRequest(
      UserType.Member,
      this.currentMember.Id,
      InviteCodeIntent.ClassSession,
      this.sessionDetails.Id,
      -1
    )).subscribe(async (data : InviteCode) => {
      this.inviteSubmitted = false;
      this.inviteCode = data;

      this.openGraphMetadata = await this.inviteCodeService.getOpenGraphMetadata(this.inviteCode.Code);

      //open invite friends popup
      const modalRef =this.modalService.open(InviteComponent, {
        ariaLabelledBy: 'modal-basic-title', 
        size: 'md',
        centered: true,
        fullscreen: window.innerWidth <= 768,
        windowClass: 'invite-modal-wrapper',
        modalDialogClass: 'invite-modal-dialog'
      });

      const sessionTimeZone = await this.getSessionTimeZone();
      const display = Utils.formatSessionDisplayParts(
        this.sessionDetails.StartDate,
        this.sessionDetails.StartTime,
        this.sessionDetails.EndTime,
        sessionTimeZone
      );
      const whenLabel = display.timeZoneAbbr
        ? `${display.dayOfWeek}, ${display.month} ${display.dayOfMonth} at ${display.startTimeFull} ${display.timeZoneAbbr}`
        : `${display.dayOfWeek}, ${display.month} ${display.dayOfMonth} at ${display.startTimeFull}`;
      var title = this.sessionDetails.Studio.Name + " - " + this.sessionDetails.Class.Name + " at " + this._environmentService.getFacilityConfig().name + " " + whenLabel;
      var emailTitle = 'Hey! Join me at ' + this.sessionDetails.Studio.Name + '!🙌';
      var emailBody = 'Hey! Join me for ' + this.sessionDetails.Class.Name + ' at ' + this.sessionDetails.Studio.Name + ' on ' + whenLabel + ' inside ' + this._environmentService.getFacilityConfig().name + '. Use the link below to book your spot! ' + this.getSharerUrl();
      //Passing InviteCode and InviteDialogModel to popup
      modalRef.componentInstance.inviteCode = this.inviteCode;
      modalRef.componentInstance.inviteDialogModel = new InviteDialogModel(
        this.openGraphMetadata.Title,
        this.openGraphMetadata.Description,
        this.openGraphMetadata.ImageUrl,
        this.openGraphMetadata.Url,
        emailTitle,
        emailBody
      );

      //Retrieving result from popup
      modalRef.result.then((result: void) => {
          this.logService.debug("invite popup closed:");
          this.inviteSubmitted = false;
      });
    });

    
  }

  getSharerUrl() : string
  {
    return this._environmentService.getFacilityConfig().sharerBaseUrl + "share?invite_code=" + this.inviteCode.Code + "&location_id=" + 1
  }

  getDirectionUrl(): string {
    return `https://www.google.com/maps/dir/?api=1&destination=${this.sessionDetails?.Class?.Address?.Latitude},${this.sessionDetails?.Class?.Address?.Longitude}`;
  }

  async generateICS() {
    const sessionTimeZone = await this.getSessionTimeZone();
    const { start, end } = Utils.combineSessionStartAndEnd(
      this.sessionDetails.StartDate,
      this.sessionDetails.StartTime,
      this.sessionDetails.EndTime,
      sessionTimeZone
    );
    const startDateTimeArray: DateArray = Utils.toIcsDateTimeParts(start);
    const endDateTimeArray: DateArray = Utils.toIcsDateTimeParts(end);

    const event: EventAttributes = {
      start: startDateTimeArray,
      startInputType: 'utc',
      end: endDateTimeArray,
      endInputType: 'utc',
      title: this.sessionDetails.Class.Name + " at " + this.sessionDetails.Studio.Name,
      description: this.sessionDetails.Class.Name + " at " + this.sessionDetails.Studio.Name,
      location: this.address,
      url: window.location.origin + this.router.url,
      status: 'CONFIRMED',
      organizer: {
        name: this.sessionDetails.InstructorName,
        email: this._environmentService.getFacilityConfig().supportEmail
      }
    };
    
    createEvent(event, (error, value) => {
      if (error) {
        this.logService.error(error);
        return;
      }
  
      const blob = new Blob([value], { type: 'text/calendar' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'df-event-' + uuidv4() + '.ics';
      link.click();
    });
  }
  
  
  

  private extractErrorMessage(error: any): string {
    // Handle different error response formats
    if (typeof error === 'string') {
      return error;
    }
    // Check for nested error structure (error.error.message)
    if (error?.error?.message) {
      return error.error.message;
    }
    // Check for direct message property
    if (error?.message) {
      return error.message;
    }
    // Check if error.error is a string
    if (error?.error && typeof error.error === 'string') {
      return error.error;
    }
    // Default fallback
    return 'An error occurred while booking. Please try again.';
  }

  async getAddress(): Promise<string> {
    var address : string = "";
    const geocoder = new google.maps.Geocoder();
    const latLng = new google.maps.LatLng(this._environmentService.getFacilityConfig().googleMap.latitude, this._environmentService.getFacilityConfig().googleMap.longitude);

    return new Promise<string>((resolve, reject) => {
      geocoder.geocode({ location: latLng }, (results: any, status: any) => {
        if (status === "OK") {
          if (results[0]) {
            const address = results[0].formatted_address.replace("Inside ", "");
            resolve(address);
          } else {
            this.logService.debug("Geocoder: No results found");
            reject("No results found");
          }
        } else {
          this.logService.debug("Geocoder failed due to: " + status);
          reject(status);
        }
      });
    });
  }
}
